import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './HeroSection.css';

gsap.registerPlugin(ScrollTrigger);

// Character data
const characters = {
  dante: {
    name: 'DANTE',
    title: ['DEVIL', 'HUNTER'],
    badge: 'LEGENDARY DEVIL HUNTER',
    subtitle: 'UNLEASH YOUR INNER DEMON,\nBECOME THE ULTIMATE HUNTER',
    image: '/images/dante.png',
    color: '#c41e3a',
    colorLight: '#ff6b6b',
    bgGradient: 'linear-gradient(135deg, #1a0a0a 0%, #2d0a0a 50%, #0a0a0a 100%)',
    stats: [
      { value: 'SSS', label: 'STYLE RANK' },
      { value: '666+', label: 'DEMONS SLAIN' },
      { value: '∞', label: 'COMBOS' }
    ],
    skills: [
      { name: 'SWORDMASTER', color: '#c41e3a' },
      { name: 'GUNSLINGER', color: '#ff6b6b' },
      { name: 'TRICKSTER', color: '#c41e3a' },
      { name: 'ROYAL GUARD', color: '#ff6b6b' },
      { name: 'DEVIL TRIGGER', color: '#c41e3a' }
    ],
    trustedText: 'TRUSTED BY 1,000,000+ HUNTERS'
  },
  vergil: {
    name: 'VERGIL',
    title: ['DARK', 'SLAYER'],
    badge: 'LEGENDARY DARK SLAYER',
    subtitle: 'I NEED MORE POWER.\nFOOLISHNESS, DANTE.',
    image: '/images/vergil/vergil.png',
    color: '#1e90ff',
    colorLight: '#4169e1',
    bgGradient: 'linear-gradient(135deg, #0a0a1a 0%, #0a1a2d 50%, #0a0a1a 100%)',
    stats: [
      { value: 'SSS', label: 'MOTIVATION' },
      { value: '100%', label: 'POWER' },
      { value: 'HIGH', label: 'CONCENTRATION' }
    ],
    skills: [
      { name: 'DARK SLAYER', color: '#1e90ff' },
      { name: 'YAMATO', color: '#4169e1' },
      { name: 'JUDGEMENT CUT', color: '#1e90ff' },
      { name: 'RAPID SLASH', color: '#4169e1' },
      { name: 'SIN DEVIL TRIGGER', color: '#1e90ff' }
    ],
    trustedText: 'FEARED BY ALL DEMONS'
  }
};

function HeroSection({ character: charProp = 'dante' }) {
  const [activeCharacter, setActiveCharacter] = useState(charProp);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  
  const character = characters[activeCharacter];

  // Mouse parallax
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const parallaxX = useTransform(mouseX, [-1, 1], [-20, 20]);
  const parallaxY = useTransform(mouseY, [-1, 1], [-20, 20]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const x = (clientX / innerWidth) * 2 - 1;
      const y = (clientY / innerHeight) * 2 - 1;
      mouseX.set(x);
      mouseY.set(y);
      setMousePosition({ x: clientX, y: clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  useEffect(() => {
    // GSAP Timeline for hero animations
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    // Split text animation for title
    const titleChars = titleRef.current.querySelectorAll('.char');
    
    tl.fromTo(titleChars,
      { opacity: 0, y: 100, rotationX: -90 },
      { 
        opacity: 1, 
        y: 0, 
        rotationX: 0,
        duration: 1,
        stagger: 0.05,
        ease: 'back.out(1.7)'
      }
    )

    .fromTo(subtitleRef.current,
      { opacity: 0, y: 30, filter: 'blur(10px)' },
      { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8 },
      '-=0.5'
    )

    .fromTo(imageRef.current,
      { opacity: 0, x: 100, scale: 0.8, rotationY: 45 },
      { opacity: 1, x: 0, scale: 1, rotationY: 0, duration: 1.5 },
      '-=0.8'
    );

    // Floating animation for character
    gsap.to(imageRef.current, {
      y: -20,
      duration: 3,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    });

    // Glitch effect on title
    const glitchInterval = setInterval(() => {
      if (titleRef.current) {
        gsap.to(titleRef.current, {
          x: Math.random() * 10 - 5,
          duration: 0.1,
          repeat: 3,
          yoyo: true,
          onComplete: () => gsap.set(titleRef.current, { x: 0 })
        });
      }
    }, 3000);

    // Glitch effect on character image
    const imageGlitchInterval = setInterval(() => {
      if (imageRef.current) {
        gsap.to(imageRef.current, {
          x: Math.random() * 20 - 10,
          skewX: Math.random() * 10 - 5,
          duration: 0.05,
          repeat: 5,
          yoyo: true,
          onComplete: () => gsap.set(imageRef.current, { x: 0, skewX: 0 })
        });
      }
    }, 2500);

    // Scroll-triggered animations
    ScrollTrigger.create({
      trigger: heroRef.current,
      start: 'top top',
      end: 'bottom top',
      scrub: 1,
      onUpdate: (self) => {
        gsap.to(imageRef.current, {
          y: self.progress * 100,
          opacity: 1 - self.progress * 0.5
        });
      }
    });

    return () => {
      clearInterval(glitchInterval);
      clearInterval(imageGlitchInterval);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 1.5
      }
    }
  };

  const tagVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { type: 'spring', stiffness: 300 }
    }
  };

  // Split text into characters for animation
  const splitText = (text) => {
    return text.split('').map((char, i) => (
      <span key={i} className="char" style={{ display: 'inline-block' }}>
        {char === ' ' ? '\u00A0' : char}
      </span>
    ));
  };

  // Handle character switch
  const switchCharacter = (charKey) => {
    if (charKey === activeCharacter) return;
    setActiveCharacter(charKey);
  };

  return (
    <section ref={heroRef} className={`hero hero-${activeCharacter}`} style={{ background: character.bgGradient }}>
      {/* Animated Background Elements */}
      <div className="hero-bg-effects">
        <motion.div 
          className="energy-wave wave-1"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.1, 0.3]
          }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div 
          className="energy-wave wave-2"
          animate={{
            scale: [1, 1.8, 1],
            opacity: [0.2, 0.05, 0.2]
          }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
        <motion.div 
          className="energy-wave wave-3"
          animate={{
            scale: [1, 2, 1],
            opacity: [0.15, 0.03, 0.15]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="floating-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      {/* Mouse follower light */}
      <motion.div 
        className="mouse-light"
        animate={{
          x: mousePosition.x - 100,
          y: mousePosition.y - 100
        }}
        transition={{ type: 'spring', damping: 30, stiffness: 200 }}
      />

      <div className="hero-container">
        {/* Left Content */}
        <motion.div 
          className="hero-content"
          style={{ x: parallaxX, y: parallaxY }}
        >
          <motion.div 
            className="hero-badge"
            initial={{ opacity: 0, x: -30, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.6, type: 'spring' }}
            whileHover={{ scale: 1.05, boxShadow: `0 0 30px ${character.color}` }}
            style={{ borderColor: character.color }}
          >
            <motion.span 
              className="badge-icon visual-icon lightning-icon"
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            >
            </motion.span>
            <span style={{ color: character.color }}>{character.badge}</span>
          </motion.div>

          <p ref={subtitleRef} className="hero-subtitle">
            {character.subtitle.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < character.subtitle.split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>



          {/* Audio Visualizer Style Element */}
          <motion.div 
            className="audio-visualizer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            <div className="visualizer-icon">●</div>
            <div className="visualizer-bars">
              {[...Array(20)].map((_, i) => (
                <motion.div 
                  key={i} 
                  className="bar"
                  animate={{ 
                    height: [10, Math.random() * 30 + 10, 10],
                  }}
                  transition={{ 
                    duration: 0.5 + Math.random() * 0.5,
                    repeat: Infinity,
                    repeatType: 'reverse'
                  }}
                />
              ))}
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.button 
            className="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.6 }}
            whileHover={{ 
              scale: 1.05, 
              boxShadow: '0 0 60px rgba(196, 30, 58, 1)',
              textShadow: '0 0 20px rgba(255, 255, 255, 0.8)'
            }}
            whileTap={{ scale: 0.95 }}
          >
            <motion.span
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              BEGIN YOUR MISSION
            </motion.span>
            <motion.span 
              className="cta-icon"
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              ⚔
            </motion.span>
          </motion.button>

          {/* Stats */}
          <motion.div 
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.6 }}
          >
            {character.stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat"
                whileHover={{ scale: 1.1, y: -5 }}
              >
                <motion.span 
                  className="stat-value"
                  style={{ color: character.color }}
                  animate={{ 
                    textShadow: [
                      `0 0 10px ${character.color}80`,
                      `0 0 20px ${character.color}`,
                      `0 0 10px ${character.color}80`
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {stat.value}
                </motion.span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Center Character with Title Overlay */}
        <div className="hero-center">
          <motion.div 
            className="character-glow"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          <motion.div 
            ref={imageRef} 
            className="character-image"
            style={{ 
              x: useTransform(mouseX, [-1, 1], [-10, 10]),
              y: useTransform(mouseY, [-1, 1], [-10, 10])
            }}
            whileHover={{ scale: 1.05 }}
          >
            <img src={character.image} alt={character.name} className="dante-img" />
            
            {/* Energy rings around character */}
            <motion.div
              className="energy-ring ring-1"
              animate={{ rotate: 360 }}
              transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="energy-ring ring-2"
              animate={{ rotate: -360 }}
              transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
            />
          </motion.div>
          <h1 ref={titleRef} className="center-title">
            <span className="title-line">{splitText(character.title[0])}</span>
            <span className="title-line">{splitText(character.title[1])}</span>
          </h1>
        </div>

        {/* Right Content - Tags */}
        <motion.div 
          className="hero-tags"
          style={{ 
            x: useTransform(mouseX, [-1, 1], [10, -10]),
            y: useTransform(mouseY, [-1, 1], [10, -10])
          }}
        >
          <motion.div 
            className="trusted-badge"
            initial={{ opacity: 0, x: 30, rotateY: 45 }}
            animate={{ opacity: 1, x: 0, rotateY: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            whileHover={{ scale: 1.05, boxShadow: `0 10px 40px ${character.color}50` }}
          >
            <div className="trusted-avatars">
              <div className="avatar" style={{ backgroundColor: character.color }}></div>
              <div className="avatar" style={{ backgroundColor: character.colorLight }}></div>
              <div className="avatar" style={{ backgroundColor: character.color }}></div>
            </div>
            <div className="trusted-text">
              <span className="trusted-title" style={{ color: character.color }}>{character.trustedText}</span>
              <span className="trusted-sub">— THE SON OF SPARDA</span>
            </div>
          </motion.div>

          <motion.div 
            className="skill-tags"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {character.skills.map((tag, index) => (
              <motion.span 
                key={index} 
                className="skill-tag"
                variants={tagVariants}
                whileHover={{ 
                  scale: 1.15, 
                  backgroundColor: tag.color,
                  color: '#fff',
                  boxShadow: `0 0 20px ${tag.color}`,
                  rotate: [0, -5, 5, 0]
                }}
                whileTap={{ scale: 0.95 }}
                style={{ borderColor: tag.color, color: tag.color }}
                animate={{
                  borderColor: [tag.color, '#fff', tag.color]
                }}
                transition={{
                  borderColor: { duration: 2, repeat: Infinity, delay: index * 0.2 }
                }}
              >
                {tag.name}
              </motion.span>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="hero-decoration">
        <motion.div 
          className="deco-line left"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 2, duration: 1 }}
        />
        <motion.div 
          className="deco-line right"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 2.2, duration: 1 }}
        />
        <motion.div 
          className="deco-circle"
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 20, repeat: Infinity, ease: 'linear' },
            scale: { duration: 3, repeat: Infinity }
          }}
        />
      </div>

      {/* Lightning effects */}
      <motion.div
        className="lightning-effect"
        animate={{
          opacity: [0, 1, 0],
          scale: [0.8, 1.2, 0.8]
        }}
        transition={{
          duration: 0.3,
          repeat: Infinity,
          repeatDelay: 5 + Math.random() * 5
        }}
      />
    </section>
  );
}

export default HeroSection;
